import { useEffect, useRef, useState } from 'react';
import { Box } from '@chakra-ui/react';
import { Lock } from 'lucide-react';
import { useSystemVarsStore } from '../../state/useSystemVarsStore';
import { useViewStore } from '../../state/commandAuxStores';
import { useAppStore } from '../../state/store';
import { useCommandInteract } from '../../core/commands/commandInteract';
import { ISO_PLANE_ANGLES, type IsoPlane } from '../../core/drafting/isoPlane';

/**
 * Drafting crosshair overlay for the 2D workspace.
 *
 * CURSORSIZE sysvar parity: the full crosshair length is a percentage of the
 * viewport (1-100). The overlay is pointer-transparent so it never steals
 * canvas events; only the runtime position/rotation is an inline style
 * (allowed for per-frame values), while line styling lives in theme tokens
 * (--color-crosshair / --color-crosshair-pending).
 *
 * ISODRAFT (Faz 7.1): with the isometric master on, the two arms rotate to
 * the active plane's axis pair (Left [90/150], Top [30/150], Right [30/90]) —
 * OCS draws the crosshair with the iso pair on model and paper.
 *
 * Faz 11.1 (OCS overlay.rs crosshair parity):
 *  - PICKBOX: a centered pick-sized square while the select tool runs or a
 *    pick step waits (drawing point steps keep the bare crosshair).
 *  - Pick-pending: while the command engine waits for a point/pick click the
 *    crosshair turns blue and the pickbox is forced to a minimum 8px box.
 *  - Locked badge: an amber padlock rides next to the cursor while the
 *    hovered entity (or its layer) is locked.
 */
export function DraftingCrosshair() {
    const cursorSize = useSystemVarsStore((s) => s.CURSORSIZE);
    const pickboxSize = useSystemVarsStore((s) => s.PICKBOX);
    const isoDrafting = useViewStore((s) => s.isoDrafting);
    const isoplane = useViewStore((s) => s.isoplane);
    const activeTool = useAppStore((s) => s.activeTool);
    const interactStepKind = useCommandInteract((s) =>
        s.active ? s.spec?.steps[s.stepIndex]?.kind ?? null : null
    );
    // Locked-hover badge: boolean selector — re-evaluates per store write but
    // re-renders only when the flipped value changes.
    const hoverLocked = useAppStore((s) => {
        const id = s.hoveredId;
        if (!id) return false;
        const e = s.entities[id];
        if (!e) return false;
        if (e.locked) return true;
        return !!s.layers[e.layer]?.locked;
    });
    const wrapRef = useRef<HTMLDivElement>(null);
    const [pos, setPos] = useState<{ x: number; y: number } | null>(null);
    const [size, setSize] = useState({ w: 0, h: 0 });

    // Track the pointer within the viewport container (capture phase so the
    // canvas listeners keep receiving their own events untouched).
    useEffect(() => {
        const el = wrapRef.current;
        if (!el) return;
        const measure = () => setSize({ w: el.clientWidth, h: el.clientHeight });
        measure();
        const ro = new ResizeObserver(measure);
        ro.observe(el);
        const onMove = (e: PointerEvent) => {
            const rect = el.getBoundingClientRect();
            if (
                e.clientX < rect.left || e.clientX > rect.right ||
                e.clientY < rect.top || e.clientY > rect.bottom
            ) {
                setPos(null);
                return;
            }
            setPos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
        };
        const onLeave = () => setPos(null);
        // The mount effect runs after React attaches refs, so `el` is valid.
        window.addEventListener('pointermove', onMove, true);
        el.addEventListener('pointerleave', onLeave);
        return () => {
            ro.disconnect();
            window.removeEventListener('pointermove', onMove, true);
            el.removeEventListener('pointerleave', onLeave);
        };
    }, []);

    const arm = (Math.min(size.w, size.h) * Math.max(1, Math.min(100, cursorSize))) / 200;
    if (!pos || arm < 2) return null;

    // Pick-pending (OCS): a waiting point/pick step paints the crosshair blue
    // and forces at least an 8px box. Outside commands the pickbox only rides
    // with the select tool (drawing steps keep the bare crosshair).
    const pending = interactStepKind === 'point' || interactStepKind === 'pick';
    const selectTool = String(activeTool ?? '').toLowerCase() === 'select';
    const boxHalf = pending
        ? Math.max(pickboxSize, 4)
        : selectTool
            ? pickboxSize
            : 0;

    // Screen Y points down: a CCW world angle renders as -angle here. With
    // isodraft off the arms stay at the 0°/90° cross.
    const plane: IsoPlane = isoplane === 'off' ? 'left' : isoplane;
    const [a1, a2] = ISO_PLANE_ANGLES[plane];
    const r1 = isoDrafting ? (a1 * Math.PI) / 180 : 0;
    const r2 = isoDrafting ? (a2 * Math.PI) / 180 : Math.PI / 2;

    // Crosshair ink comes from the theme tokens (light mode: dark ink; dark
    // mode: translucent white) — the OCS adapt_to_bg counterpart.
    const ink = pending ? 'var(--color-crosshair-pending)' : 'var(--color-crosshair)';

    const bar = (worldAngle: number, key: string) => {
        const screenDeg = -(worldAngle * 180) / Math.PI;
        const len = arm * 2;
        return (
            <Box
                key={key}
                position="absolute"
                left={`${pos.x - len / 2}px`}
                top={`${pos.y - 0.5}px`}
                w={`${len}px`}
                h="1px"
                bg={ink}
                transformOrigin="center center"
                transform={`rotate(${screenDeg}deg)`}
            />
        );
    };

    return (
        <Box ref={wrapRef} position="absolute" inset="0" pointerEvents="none" zIndex="1">
            {bar(r1, 'a')}
            {bar(r2, 'b')}
            {boxHalf > 0 && (
                <Box
                    position="absolute"
                    left={`${pos.x - boxHalf}px`}
                    top={`${pos.y - boxHalf}px`}
                    w={`${boxHalf * 2}px`}
                    h={`${boxHalf * 2}px`}
                    border="1px solid"
                    borderColor={ink}
                    bg={pending ? 'var(--color-crosshair-pending)' : 'transparent'}
                    opacity={pending ? 0.35 : 1}
                />
            )}
            {hoverLocked && (
                <Box
                    position="absolute"
                    left={`${pos.x + 12}px`}
                    top={`${pos.y - 22}px`}
                    color="var(--color-badge-locked)"
                    display="flex"
                    alignItems="center"
                >
                    <Lock size={12} strokeWidth={2.5} />
                </Box>
            )}
        </Box>
    );
}
